import React, { useState } from 'react'
import { Formik, Form } from "formik";
import * as Yup from "yup";
import style from "./style.module.scss"
import Modal from "../../../components/modal"
import Button from "../../../components/new-button"
import TextInput from "../../../components/form/text-input"
import { updatePassword, Logout } from "../../../../api"
import useUserStore from "../../../stores/user.js";
import { toast } from "react-hot-toast";
import { withRouter } from "react-router-dom";

// Validation schema based on password requirements
const validationSchema = Yup.object({
  new_password: Yup.string()
    .required("New password is required")
    .min(14, "1") // Password must be at least 14 characters
    .matches(/[A-Z]/, "2") // Password must contain at least one uppercase letter
    .matches(/[a-z]/, "3") // Password must contain at least one lowercase letter
    .matches(/[0-9]/, "4") // Password must contain at least one number
    .matches(/[\-!@#$%^&*()_+\[\]{}|;:,.<>?]/, "5"), // Password must contain at least one special character
  confirm_password: Yup.string()
    .required("Please confirm your password")
    .oneOf([Yup.ref('new_password'), null], "Passwords must match")
});

const validationErrorMessagesMapping = {
  "1": "Password must be at least 14 characters",
  "2": "Password must contain at least one uppercase letter",
  "3": "Password must contain at least one lowercase letter",
  "4": "Password must contain at least one number",
  "5": "Password must contain at least one special character",
};

function NewPassword({ isOpen, onClose, history }) {
  const initialValues = {
    new_password: "",
    confirm_password: "",
  };

  const user = useUserStore((state) => state.user);
  const [passwordChanged, setPasswordChanged] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      // Handle form submission here
      const res = await updatePassword({
        id: user.id,
        password: values.new_password,
      });
      if (res.success) {
        toast.success("Password updated successfully", { duration: 5000 });
        setPasswordChanged(true);
        Logout().then(() => {
          setTimeout(() => {
            window.location.href = "/";
          }, 2 * 1000);
        }).catch((error) => {
          toast.error("Error logging out", { duration: 5000 });
        });
      } else {
        toast.error(res.message, { duration: 5000 });
      }

      // reset form on success
      resetForm();
    } catch (error) {
      console.error("Error updating password:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} header={passwordChanged ? "" : "Confirm Password"} size="sm">

      {passwordChanged ? (
        <div className={style.password_changed}>

          <div className={style.password_changed_body}>
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="48" height="48" rx="24" fill="#37B450" fill-opacity="0.1" />
              <rect x="4" y="4" width="40" height="40" rx="20" fill="#37B450" fill-opacity="0.4" />
              <rect x="8" y="8" width="32" height="32" rx="16" fill="#37B450" />
              <path d="M22.2859 26.7185L30.1648 18.8387L31.3776 20.0507L22.2859 29.1425L16.8311 23.6876L18.0431 22.4756L22.2859 26.7185Z" fill="white" />
            </svg>

            <span className={style.password_changed_title}>Password updated successfully</span>

            <span className={style.password_changed_description}>Please log in to your account using your new password</span>
          </div>

          <div className={style.password_changed_footer}>
            <Button
              type="button"
              variant="primary"
              onClick={() => {
                onClose();
                window.location.href = "/";
              }}
            >
              <span>Log in</span>
            </Button>
          </div>

        </div>
      ) : (
        <>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched, isSubmitting, handleChange, values }) => (
              <Form>
                <div className={style.body}>
                  <TextInput
                    label="New Password"
                    name="new_password"
                    placeholder="Enter new password"
                    type={showPassword ? "text" : "password"}
                    error={errors.new_password ? validationErrorMessagesMapping[errors.new_password] : null}
                    touched={touched.new_password}
                    errored={errors.new_password}
                    onChange={handleChange}
                    value={values.new_password}
                    suffix={
                      <span className={style.eye_icon}
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M3.36999 2.16379C3.32608 2.1143 3.27279 2.07401 3.21319 2.04526C3.1536 2.01651 3.08889 1.99988 3.02282 1.99633C2.95675 1.99277 2.89063 2.00237 2.8283 2.02455C2.76597 2.04674 2.70865 2.08108 2.65969 2.12558C2.61072 2.17008 2.57108 2.22385 2.54305 2.28379C2.51502 2.34372 2.49916 2.40863 2.4964 2.47473C2.49364 2.54084 2.50402 2.60684 2.52696 2.6689C2.54989 2.73097 2.58491 2.78786 2.62999 2.83629L3.83249 4.15942C1.56249 5.55254 0.586244 7.70004 0.543119 7.79754C0.51469 7.86149 0.5 7.93069 0.5 8.00067C0.5 8.07065 0.51469 8.13985 0.543119 8.20379C0.564994 8.25317 1.09437 9.42692 2.27124 10.6038C3.83937 12.1713 5.81999 13 7.99999 13C9.12038 13.0064 10.2294 12.7758 11.2544 12.3232L12.6294 13.8363C12.6733 13.8858 12.7266 13.9261 12.7862 13.9548C12.8458 13.9836 12.9105 14.0002 12.9765 14.0038C13.0426 14.0073 13.1087 13.9977 13.1711 13.9755C13.2334 13.9533 13.2907 13.919 13.3397 13.8745C13.3886 13.83 13.4283 13.7762 13.4563 13.7163C13.4843 13.6564 13.5002 13.5915 13.503 13.5254C13.5057 13.4592 13.4953 13.3932 13.4724 13.3312C13.4495 13.2691 13.4145 13.2122 13.3694 13.1638L3.36999 2.16379ZM6.32812 6.90379L8.93249 9.76942C8.5403 9.97574 8.09069 10.046 7.65426 9.96907C7.21783 9.89217 6.81931 9.67249 6.52126 9.34455C6.22321 9.0166 6.04252 8.59895 6.00756 8.15718C5.9726 7.71541 6.08536 7.27453 6.32812 6.90379ZM7.99999 12C6.07624 12 4.39562 11.3007 3.00437 9.92192C2.43332 9.35441 1.94763 8.70704 1.56249 8.00004C1.85562 7.45067 2.79124 5.91317 4.52187 4.91379L5.64687 6.14817C5.21133 6.70598 4.98702 7.39981 5.01358 8.10702C5.04014 8.81423 5.31586 9.48929 5.79202 10.0129C6.26817 10.5364 6.91409 10.8748 7.61561 10.9682C8.31713 11.0616 9.02908 10.904 9.62562 10.5232L10.5462 11.5357C9.73374 11.8474 8.87023 12.0049 7.99999 12ZM8.37499 6.03567C8.24471 6.0108 8.12963 5.9352 8.05509 5.82549C7.98054 5.71578 7.95263 5.58095 7.97749 5.45067C8.00236 5.32038 8.07796 5.20531 8.18767 5.13076C8.29738 5.05621 8.43221 5.0283 8.56249 5.05317C9.19975 5.17671 9.77999 5.50295 10.2167 5.98322C10.6534 6.46349 10.9231 7.07207 10.9856 7.71817C10.998 7.85019 10.9574 7.98172 10.8727 8.08381C10.7881 8.1859 10.6664 8.25019 10.5344 8.26254C10.5188 8.26346 10.5031 8.26346 10.4875 8.26254C10.3625 8.26308 10.2419 8.21679 10.1493 8.13281C10.0568 8.04882 9.99905 7.93323 9.98749 7.80879C9.94541 7.37904 9.76575 6.97434 9.47522 6.65488C9.1847 6.33542 8.79883 6.11825 8.37499 6.03567ZM15.455 8.20379C15.4287 8.26254 14.7956 9.66442 13.37 10.9413C13.3213 10.9863 13.2641 11.0212 13.2018 11.0439C13.1395 11.0666 13.0732 11.0767 13.007 11.0735C12.9407 11.0704 12.8758 11.0541 12.8159 11.0256C12.756 10.9971 12.7023 10.957 12.6581 10.9076C12.6138 10.8582 12.5799 10.8004 12.5582 10.7377C12.5365 10.6751 12.5274 10.6087 12.5316 10.5425C12.5358 10.4763 12.5531 10.4116 12.5826 10.3522C12.612 10.2927 12.653 10.2397 12.7031 10.1963C13.4025 9.56793 13.9901 8.82527 14.4406 8.00004C14.0546 7.2924 13.5679 6.64459 12.9956 6.07692C11.6044 4.69942 9.92374 4.00004 7.99999 4.00004C7.59465 3.99955 7.18996 4.03237 6.78999 4.09817C6.72495 4.10967 6.65828 4.10817 6.59382 4.09376C6.52936 4.07934 6.46839 4.0523 6.41444 4.01419C6.3605 3.97609 6.31463 3.92767 6.2795 3.87173C6.24437 3.8158 6.22067 3.75346 6.20978 3.68831C6.19888 3.62316 6.20099 3.55651 6.216 3.49218C6.23101 3.42786 6.25862 3.36715 6.29723 3.31356C6.33583 3.25996 6.38468 3.21455 6.44094 3.17994C6.49719 3.14533 6.55975 3.12221 6.62499 3.11192C7.07947 3.03688 7.53936 2.99946 7.99999 3.00004C10.18 3.00004 12.1606 3.82879 13.7287 5.39692C14.9056 6.57379 15.435 7.74817 15.4569 7.79754C15.4853 7.86149 15.5 7.93069 15.5 8.00067C15.5 8.07065 15.4853 8.13985 15.4569 8.20379H15.455Z" fill="#3A383F" />
                        </svg>
                      </span>
                    }
                  />

                  <div className={style.password_rules}>
                    <span>Password must contain</span>
                    <ul>
                      <li className={errors.new_password && errors.new_password.includes('1') ? style.error : ''} >minimum 14 characters</li>
                      <li className={errors.new_password && (errors.new_password.includes('2') || errors.new_password.includes('3')) ? style.error : ''} >both lower and uppercase letters</li>
                      <li className={errors.new_password && (errors.new_password.includes('4') || errors.new_password.includes('5')) ? style.error : ''} >number and special character</li>
                    </ul>
                  </div>

                  <TextInput
                    label="Repeat New Password"
                    name="confirm_password"
                    placeholder="Repeat new password"
                    type={showConfirmPassword ? "text" : "password"}
                    error={touched.confirm_password && errors.confirm_password ? errors.confirm_password : null}
                    touched={touched.confirm_password}
                    errored={errors.confirm_password}
                    onChange={handleChange}
                    value={values.confirm_password}
                    suffix={
                      <span className={style.eye_icon}
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M3.36999 2.16379C3.32608 2.1143 3.27279 2.07401 3.21319 2.04526C3.1536 2.01651 3.08889 1.99988 3.02282 1.99633C2.95675 1.99277 2.89063 2.00237 2.8283 2.02455C2.76597 2.04674 2.70865 2.08108 2.65969 2.12558C2.61072 2.17008 2.57108 2.22385 2.54305 2.28379C2.51502 2.34372 2.49916 2.40863 2.4964 2.47473C2.49364 2.54084 2.50402 2.60684 2.52696 2.6689C2.54989 2.73097 2.58491 2.78786 2.62999 2.83629L3.83249 4.15942C1.56249 5.55254 0.586244 7.70004 0.543119 7.79754C0.51469 7.86149 0.5 7.93069 0.5 8.00067C0.5 8.07065 0.51469 8.13985 0.543119 8.20379C0.564994 8.25317 1.09437 9.42692 2.27124 10.6038C3.83937 12.1713 5.81999 13 7.99999 13C9.12038 13.0064 10.2294 12.7758 11.2544 12.3232L12.6294 13.8363C12.6733 13.8858 12.7266 13.9261 12.7862 13.9548C12.8458 13.9836 12.9105 14.0002 12.9765 14.0038C13.0426 14.0073 13.1087 13.9977 13.1711 13.9755C13.2334 13.9533 13.2907 13.919 13.3397 13.8745C13.3886 13.83 13.4283 13.7762 13.4563 13.7163C13.4843 13.6564 13.5002 13.5915 13.503 13.5254C13.5057 13.4592 13.4953 13.3932 13.4724 13.3312C13.4495 13.2691 13.4145 13.2122 13.3694 13.1638L3.36999 2.16379ZM6.32812 6.90379L8.93249 9.76942C8.5403 9.97574 8.09069 10.046 7.65426 9.96907C7.21783 9.89217 6.81931 9.67249 6.52126 9.34455C6.22321 9.0166 6.04252 8.59895 6.00756 8.15718C5.9726 7.71541 6.08536 7.27453 6.32812 6.90379ZM7.99999 12C6.07624 12 4.39562 11.3007 3.00437 9.92192C2.43332 9.35441 1.94763 8.70704 1.56249 8.00004C1.85562 7.45067 2.79124 5.91317 4.52187 4.91379L5.64687 6.14817C5.21133 6.70598 4.98702 7.39981 5.01358 8.10702C5.04014 8.81423 5.31586 9.48929 5.79202 10.0129C6.26817 10.5364 6.91409 10.8748 7.61561 10.9682C8.31713 11.0616 9.02908 10.904 9.62562 10.5232L10.5462 11.5357C9.73374 11.8474 8.87023 12.0049 7.99999 12ZM8.37499 6.03567C8.24471 6.0108 8.12963 5.9352 8.05509 5.82549C7.98054 5.71578 7.95263 5.58095 7.97749 5.45067C8.00236 5.32038 8.07796 5.20531 8.18767 5.13076C8.29738 5.05621 8.43221 5.0283 8.56249 5.05317C9.19975 5.17671 9.77999 5.50295 10.2167 5.98322C10.6534 6.46349 10.9231 7.07207 10.9856 7.71817C10.998 7.85019 10.9574 7.98172 10.8727 8.08381C10.7881 8.1859 10.6664 8.25019 10.5344 8.26254C10.5188 8.26346 10.5031 8.26346 10.4875 8.26254C10.3625 8.26308 10.2419 8.21679 10.1493 8.13281C10.0568 8.04882 9.99905 7.93323 9.98749 7.80879C9.94541 7.37904 9.76575 6.97434 9.47522 6.65488C9.1847 6.33542 8.79883 6.11825 8.37499 6.03567ZM15.455 8.20379C15.4287 8.26254 14.7956 9.66442 13.37 10.9413C13.3213 10.9863 13.2641 11.0212 13.2018 11.0439C13.1395 11.0666 13.0732 11.0767 13.007 11.0735C12.9407 11.0704 12.8758 11.0541 12.8159 11.0256C12.756 10.9971 12.7023 10.957 12.6581 10.9076C12.6138 10.8582 12.5799 10.8004 12.5582 10.7377C12.5365 10.6751 12.5274 10.6087 12.5316 10.5425C12.5358 10.4763 12.5531 10.4116 12.5826 10.3522C12.612 10.2927 12.653 10.2397 12.7031 10.1963C13.4025 9.56793 13.9901 8.82527 14.4406 8.00004C14.0546 7.2924 13.5679 6.64459 12.9956 6.07692C11.6044 4.69942 9.92374 4.00004 7.99999 4.00004C7.59465 3.99955 7.18996 4.03237 6.78999 4.09817C6.72495 4.10967 6.65828 4.10817 6.59382 4.09376C6.52936 4.07934 6.46839 4.0523 6.41444 4.01419C6.3605 3.97609 6.31463 3.92767 6.2795 3.87173C6.24437 3.8158 6.22067 3.75346 6.20978 3.68831C6.19888 3.62316 6.20099 3.55651 6.216 3.49218C6.23101 3.42786 6.25862 3.36715 6.29723 3.31356C6.33583 3.25996 6.38468 3.21455 6.44094 3.17994C6.49719 3.14533 6.55975 3.12221 6.62499 3.11192C7.07947 3.03688 7.53936 2.99946 7.99999 3.00004C10.18 3.00004 12.1606 3.82879 13.7287 5.39692C14.9056 6.57379 15.435 7.74817 15.4569 7.79754C15.4853 7.86149 15.5 7.93069 15.5 8.00067C15.5 8.07065 15.4853 8.13985 15.4569 8.20379H15.455Z" fill="#3A383F" />
                        </svg>
                      </span>
                    }
                  />
                </div>

                <div className={style.footer}>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={onClose}
                    disabled={isSubmitting}
                  >
                    <span>Cancel</span>
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={isSubmitting}
                    isLoading={isSubmitting}
                  >
                    <span>{isSubmitting ? "Processing..." : "Confirm"}</span>
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </>
      )}

    </Modal>
  )
}

export default withRouter(NewPassword)